import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { listAllFeedback, type WidgetApiConfig } from "../api/client";
import { cacheInvalidate } from "../api/cache";
import { readProfile, type WidgetRole } from "../api/profile";
import { markTourSeen, tourSeen } from "../api/tour";
import { ProfileMode } from "./ProfileMode";
import { RecordingMode } from "./RecordingMode";
import { ScreenshotMode } from "./ScreenshotMode";
import { Tooltip } from "./Tooltip";
import { ViewAllMode } from "./ViewAllMode";
import { WidgetTour } from "./WidgetTour";
import { ChatIcon, DragIcon, ScreenshotIcon, UserIcon, VideoIcon } from "./icons";

export type WidgetProps = {
  apiConfig: WidgetApiConfig;
  rootElement: HTMLElement;
  buttonLabel?: string;
  /** The feedback tool's name (from the embed's data-app-title) — shown in the tour. */
  appTitle?: string;
};

type Mode = "idle" | "screenshot" | "recording" | "viewall" | "profile";
type PillPosition = { edge: "left" | "right"; topPx: number };

const POS_STORAGE_KEY = "maua-feedback-position";
const DEFAULT_POSITION: PillPosition = { edge: "right", topPx: -1 }; // -1 = vertical center
const PILL_HEIGHT_ESTIMATE = 260;
const MIN_DRAG_PX = 6; // travel before drag is considered intentional

function readPosition(): PillPosition {
  try {
    const raw = localStorage.getItem(POS_STORAGE_KEY);
    if (!raw) return DEFAULT_POSITION;
    const parsed = JSON.parse(raw) as PillPosition;
    if (parsed && (parsed.edge === "left" || parsed.edge === "right")) return parsed;
  } catch { /* ignored */ }
  return DEFAULT_POSITION;
}

function writePosition(pos: PillPosition) {
  try { localStorage.setItem(POS_STORAGE_KEY, JSON.stringify(pos)); } catch { /* ignored */ }
}

export function Widget({ apiConfig, rootElement, appTitle }: WidgetProps) {
  const [mode, setMode] = useState<Mode>("idle");
  const [pos, setPos] = useState<PillPosition>(() => readPosition());
  const [isDragging, setIsDragging] = useState(false);
  const [unresolvedCount, setUnresolvedCount] = useState(0);
  // Drives the "TEST" badge on the pill. Read once on mount; ProfileMode keeps
  // it in sync when the tester switches role.
  const [role, setRole] = useState<WidgetRole>(() => readProfile().role);
  // First-run guided tour (shown once, then remembered in localStorage).
  const [showTour, setShowTour] = useState(false);

  // Show the tour shortly after mount if the user hasn't seen it — the small
  // delay lets the host page settle so it doesn't feel like an interruption.
  useEffect(() => {
    if (tourSeen()) return;
    const t = setTimeout(() => setShowTour(true), 1000);
    return () => clearTimeout(t);
  }, []);

  const dismissTour = useCallback(() => {
    markTourSeen();
    setShowTour(false);
  }, []);

  // Engaging with any mode counts as "seen" — close the tour and don't re-show.
  useEffect(() => {
    if (mode !== "idle" && showTour) dismissTour();
  }, [mode, showTour, dismissTour]);

  // All drag state lives in refs so event handlers read current values with
  // zero stale-closure risk, regardless of re-renders.
  const startYRef = useRef<number | null>(null); // null = not pressed
  const dragActiveRef = useRef(false); // true once pointer travels MIN_DRAG_PX

  // ── Global window listeners (attached only while pointer is down) ──────
  // Using window-level listeners instead of element-level means there is
  // ZERO listener during hover — the drag button only has onPointerDown.
  // This is why hover couldn't accidentally trigger drag before; any missed
  // pointerup is also handled by the `e.buttons === 0` guard.

  const stopListening = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    window.removeEventListener("pointermove", onWindowMove);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    window.removeEventListener("pointerup", onWindowEnd);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    window.removeEventListener("pointercancel", onWindowEnd);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Stable handler — empty deps OK because it only reads refs and stable setters.
  const onWindowMove = useCallback((e: PointerEvent) => {
    // Safety gate: if no button is pressed (pointerup was missed), clean up.
    if (e.buttons === 0) {
      startYRef.current = null;
      dragActiveRef.current = false;
      setIsDragging(false);
      stopListening();
      return;
    }
    const startY = startYRef.current;
    if (startY === null) return;

    if (!dragActiveRef.current) {
      if (Math.abs(e.clientY - startY) < MIN_DRAG_PX) return;
      dragActiveRef.current = true;
      setIsDragging(true);
    }
    const viewportH = window.innerHeight;
    const newTop = Math.max(
      8,
      Math.min(viewportH - PILL_HEIGHT_ESTIMATE - 8, e.clientY - PILL_HEIGHT_ESTIMATE / 2),
    );
    const newEdge: "left" | "right" = e.clientX > window.innerWidth / 2 ? "right" : "left";
    setPos({ edge: newEdge, topPx: newTop });
  }, [stopListening]); // eslint-disable-line react-hooks/exhaustive-deps

  const onWindowEnd = useCallback((_e: PointerEvent) => {
    stopListening();
    const wasDragging = dragActiveRef.current;
    startYRef.current = null;
    dragActiveRef.current = false;
    setIsDragging(false);
    if (wasDragging) {
      // Persist position only when an actual drag moved the pill.
      setPos((current) => { writePosition(current); return current; });
    }
  }, [stopListening]);

  // The drag handle only needs onPointerDown — move/up are handled globally.
  function onDragStart(e: PointerEvent) {
    e.preventDefault();
    startYRef.current = e.clientY;
    dragActiveRef.current = false;
    window.addEventListener("pointermove", onWindowMove);
    window.addEventListener("pointerup", onWindowEnd);
    window.addEventListener("pointercancel", onWindowEnd);
  }

  // Clean up listeners if the component unmounts mid-drag.
  useEffect(() => () => stopListening(), [stopListening]);

  // ── Badge count ────────────────────────────────────────────────────────
  const fetchCount = useCallback(async () => {
    try {
      const data = await listAllFeedback(apiConfig);
      setUnresolvedCount(data.unresolvedCount);
    } catch { /* ignore */ }
  }, [apiConfig]);

  useEffect(() => {
    void fetchCount();
    const timer = setInterval(() => void fetchCount(), 60_000);
    return () => clearInterval(timer);
  }, [fetchCount]);

  const onFeedbackSubmitted = useCallback(() => {
    cacheInvalidate(`all:${apiConfig.projectKey}`);
    cacheInvalidate(`mine:${apiConfig.projectKey}`);
    void fetchCount();
  }, [apiConfig.projectKey, fetchCount]);

  // ── Mode renders ───────────────────────────────────────────────────────
  if (mode === "screenshot") {
    return (
      <ScreenshotMode
        apiConfig={apiConfig}
        rootElement={rootElement}
        onClose={() => setMode("idle")}
        onSubmitted={onFeedbackSubmitted}
      />
    );
  }
  if (mode === "recording") {
    return (
      <RecordingMode
        apiConfig={apiConfig}
        onClose={() => setMode("idle")}
        onSubmitted={onFeedbackSubmitted}
      />
    );
  }

  // ── Pill position ──────────────────────────────────────────────────────
  const pillStyle: Record<string, string> =
    pos.topPx < 0
      ? { top: "50%", transform: "translateY(-50%)" }
      : { top: `${pos.topPx}px` };
  if (pos.edge === "right") pillStyle.right = "16px";
  else pillStyle.left = "16px";

  return (
    <>
      {mode === "viewall" && (
        <ViewAllMode
          apiConfig={apiConfig}
          pillEdge={pos.edge}
          pillTopPx={pos.topPx}
          onClose={() => setMode("idle")}
        />
      )}
      {mode === "profile" && (
        <ProfileMode
          apiConfig={apiConfig}
          pillEdge={pos.edge}
          pillTopPx={pos.topPx}
          onClose={() => setMode("idle")}
          onRoleChange={setRole}
        />
      )}
      {showTour && mode === "idle" && (
        <WidgetTour edge={pos.edge} topPx={pos.topPx} appTitle={appTitle} onDone={dismissTour} />
      )}
      <div class="mf-pill-wrap" style={pillStyle}>
        <div
          class="mf-pill"
          data-dragging={isDragging ? "true" : undefined}
          data-tour={showTour && mode === "idle" ? "true" : undefined}
        >
          {/* screenshot/recording open full-screen modes (early-returned above),
              so these buttons are never in an "active" state while the pill
              is rendered — no is-active class needed. */}
          <Tooltip text="Submit feedback using screenshot" edge={pos.edge}>
            <button
              type="button"
              class="mf-pill-btn"
              onClick={() => setMode("screenshot")}
              aria-label="Submit feedback with screenshot"
            >
              <ScreenshotIcon />
            </button>
          </Tooltip>

          <Tooltip text="Submit feedback using recording" edge={pos.edge}>
            <button
              type="button"
              class="mf-pill-btn"
              onClick={() => setMode("recording")}
              aria-label="Submit feedback with recording"
            >
              <VideoIcon />
            </button>
          </Tooltip>

          <Tooltip
            text={
              unresolvedCount > 0
                ? `View feedback · ${unresolvedCount} unresolved`
                : "View feedback"
            }
            edge={pos.edge}
          >
            <button
              type="button"
              class={`mf-pill-btn ${mode === "viewall" ? "is-active" : ""}`}
              onClick={() => setMode("viewall")}
              aria-label={`View feedback${unresolvedCount > 0 ? `, ${unresolvedCount} unresolved` : ""}`}
              style={{ position: "relative" }}
            >
              <ChatIcon />
              {unresolvedCount > 0 && (
                <span class="mf-pill-badge" aria-hidden="true">
                  {unresolvedCount > 99 ? "99+" : unresolvedCount}
                </span>
              )}
            </button>
          </Tooltip>

          <Tooltip
            text={role === "internal" ? "Profile · Internal tester" : "Profile"}
            edge={pos.edge}
          >
            <button
              type="button"
              class={`mf-pill-btn ${mode === "profile" ? "is-active" : ""}`}
              onClick={() => setMode("profile")}
              aria-label={role === "internal" ? "Profile, internal tester" : "Profile"}
              style={{ position: "relative" }}
            >
              <UserIcon />
              {role === "internal" && (
                <span class="mf-pill-test-badge" aria-hidden="true">
                  TEST
                </span>
              )}
            </button>
          </Tooltip>

          <div class="mf-pill-divider" aria-hidden="true" />

          {/* Drag handle — only onPointerDown here; move/up are on window */}
          <button
            type="button"
            class="mf-pill-drag"
            onPointerDown={onDragStart}
            aria-label="Drag to move widget"
            title="Drag to move"
          >
            <DragIcon />
          </button>
        </div>
      </div>
    </>
  );
}
